import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  createTerm as createTermService,
  updateTerm as updateTermService,
  deleteTerm as deleteTermService, 
  syncTerms as syncTermsService,
  getTerms
} from "../../services/tenant/term.service";
import { validate } from "../../utils";
import { handlerErrorV2 } from "../../utils/errors";
import { updateTermsList, updateTermsListMethod } from "../../store/actions/tenant.actions";
import isEmpty from 'lodash/isEmpty';

const useTerms = () => {
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useDispatch();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchTerms = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const response = await getTerms();
      dispatch(updateTermsList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred'});
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchTerms();
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      name: 'required',
      shortName: 'required',
      startDate: 'required',
      endDate: 'required'
    }

    const errors = validate(fieldRules, form)
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const createTerm = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await createTermService(form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateTermsListMethod('POST', response.data));
        enqueueSnackbar('Term created successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const updateTerm = async (termId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result
    };

    try {
      const response = await updateTermService(termId, form);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateTermsListMethod('PUT', response.data));
        enqueueSnackbar('Term updated successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const deleteTerm = async (termId) => {
    try {
      const response = await deleteTermService(termId);
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateTermsListMethod('DELETE', termId));
        enqueueSnackbar('Term deleted successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const syncTerms = async () => {
    try {
      const response = await syncTermsService();
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error ocurred'}
      } else {
        dispatch(updateTermsList(response.data || []));
        enqueueSnackbar('Terms synced successfully', { variant: 'success' })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  return {
    errors,
    isLoadingList,
    createTerm,
    updateTerm,
    deleteTerm,
    syncTerms
  }
};

export default useTerms;